/** Keeps a single getMany request small enough for large duplicate resolutions */
export const ASSETS_CHUNK_SIZE = 100;
export const ASSETS_CHUNKS_CONCURRENCY = 4;

/**
 * Requests assets by ids in bounded concurrent chunks and isolates chunk failures.
 * @param {string[]} ids - asset ids to request
 * @param {(chunkIds: string[]) => Promise<{ data: { assets: object[] } }>} fetchAssets - request function
 * @param {number} chunkSize - max ids per request
 * @returns {Promise<{ assets: object[], failedChunks: number }>} successful assets and failure count
 */
export default async function fetchAssetsByIdsInChunks(ids = [], fetchAssets, chunkSize = ASSETS_CHUNK_SIZE) {
  const chunks = [];
  for (let index = 0; index < ids.length; index += chunkSize) {
    chunks.push(ids.slice(index, index + chunkSize));
  }

  const assetsByChunk = Array.from({ length: chunks.length }, () => []);
  let nextChunkIndex = 0;
  let failedChunks = 0;

  async function worker() {
    while (nextChunkIndex < chunks.length) {
      const chunkIndex = nextChunkIndex;
      nextChunkIndex += 1;
      try {
        // eslint-disable-next-line no-await-in-loop
        const { data } = await fetchAssets(chunks[chunkIndex]);
        assetsByChunk[chunkIndex] = data?.assets || [];
      } catch {
        failedChunks += 1;
      }
    }
  }

  await Promise.all(
    Array.from(
      { length: Math.min(ASSETS_CHUNKS_CONCURRENCY, chunks.length) },
      () => worker(),
    ),
  );

  return {
    assets: assetsByChunk.reduce((result, chunkAssets) => [...result, ...chunkAssets], []),
    failedChunks,
  };
}
